import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings as SettingsIcon, MapPin, Clock, Phone, Car, Layers, Pencil, X, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { formatIraqiPhone } from '@/lib/utils';
import { Spinner } from '@/components/Spinner';

const DAY_LABELS: Record<string, string> = {
  sat: 'السبت',
  sun: 'الأحد',
  mon: 'الاثنين',
  tue: 'الثلاثاء',
  wed: 'الأربعاء',
  thu: 'الخميس',
  fri: 'الجمعة',
};

const DAY_ORDER = ['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri'];

function formatTime12(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'م' : 'ص';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
}

interface WorkingHours {
  [key: string]: string; // "HH:MM-HH:MM" or "closed"
}

interface ShopSettings {
  id: string;
  name: string;
  nameAr: string;
  address: string;
  addressAr: string;
  phone: string;
  whatsapp: string;
  ownerName: string;
  bayCount: number;
  estimatedWashMinutes: number;
  actualAvgWashMinutes: number | null;
  workingHours: WorkingHours;
  openTime: string;
  closeTime: string;
  sedanPrice: number;
  suvPrice: number;
  largeSuvPrice: number;
  isActive: boolean;
}

type EditSection = 'hours' | 'bays' | 'contact' | null;

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EditSection>(null);
  const [error, setError] = useState('');

  // Edit state
  const [editHours, setEditHours] = useState<WorkingHours>({});
  const [editBays, setEditBays] = useState(1);
  const [editPhone, setEditPhone] = useState('');
  const [editWhatsapp, setEditWhatsapp] = useState('');

  const { data, isLoading } = useQuery<ShopSettings>({
    queryKey: ['owner', 'settings'],
    queryFn: () => api.get('/owner/settings'),
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<{ success: boolean }>('/owner/settings', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner', 'settings'] });
      setEditing(null);
      setError('');
    },
    onError: (err: Error) => setError(err.message),
  });

  const startEdit = (section: EditSection) => {
    if (!data) return;
    setError('');
    if (section === 'hours') {
      setEditHours({ ...data.workingHours });
    } else if (section === 'bays') {
      setEditBays(data.bayCount);
    } else if (section === 'contact') {
      setEditPhone(data.phone);
      setEditWhatsapp(data.whatsapp);
    }
    setEditing(section);
  };

  const cancelEdit = () => {
    setEditing(null);
    setError('');
  };

  const saveHours = () => mutation.mutate({ workingHours: editHours });
  const saveBays = () => mutation.mutate({ bayCount: editBays });
  const saveContact = () => mutation.mutate({ phone: editPhone, whatsapp: editWhatsapp });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const shop = data;
  if (!shop) {
    return <div className="text-center py-12 text-gray-500">لا توجد بيانات</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">إعدادات المغسلة</h1>
        <p className="text-gray-500 text-sm mt-1">معلومات وإعدادات المغسلة</p>
      </div>

      {/* Status */}
      <div className={`rounded-2xl p-4 ${shop.isActive ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${shop.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className={`text-sm font-medium ${shop.isActive ? 'text-green-700' : 'text-red-700'}`}>
            {shop.isActive ? 'المغسلة مفعلة' : 'المغسلة متوقفة'}
          </span>
        </div>
      </div>

      {/* Basic Info (read-only) */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <div className="flex items-center gap-2 mb-4">
          <SettingsIcon className="w-5 h-5 text-primary-600" />
          <h2 className="text-base font-bold text-gray-900">المعلومات الأساسية</h2>
        </div>
        <div className="space-y-4">
          <InfoRow label="اسم المغسلة" value={shop.nameAr || shop.name} />
          <InfoRow label="المالك" value={shop.ownerName} />
        </div>
      </div>

      {/* Contact (editable) */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <SectionHeader
          icon={<Phone className="w-5 h-5 text-primary-600" />}
          title="معلومات الاتصال"
          editing={editing === 'contact'}
          onEdit={() => startEdit('contact')}
          onCancel={cancelEdit}
          onSave={saveContact}
          saving={mutation.isPending}
        />
        {editing === 'contact' ? (
          <div className="space-y-3">
            <EditField label="رقم الهاتف" value={editPhone} onChange={setEditPhone} dir="ltr" placeholder="07xxxxxxxxx" />
            <EditField label="واتساب" value={editWhatsapp} onChange={setEditWhatsapp} dir="ltr" placeholder="07xxxxxxxxx" />
            {error && <p className="text-red-600 text-sm">{error}</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <InfoRow label="رقم الهاتف" value={formatIraqiPhone(shop.phone)} dir="ltr" />
            {shop.whatsapp && <InfoRow label="واتساب" value={formatIraqiPhone(shop.whatsapp)} dir="ltr" />}
          </div>
        )}
      </div>

      {/* Location (read-only) */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-primary-600" />
          <h2 className="text-base font-bold text-gray-900">الموقع</h2>
        </div>
        <InfoRow label="العنوان" value={shop.addressAr || shop.address || '—'} />
      </div>

      {/* Bay Config (editable) */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <SectionHeader
          icon={<Layers className="w-5 h-5 text-primary-600" />}
          title="أماكن الغسيل"
          editing={editing === 'bays'}
          onEdit={() => startEdit('bays')}
          onCancel={cancelEdit}
          onSave={saveBays}
          saving={mutation.isPending}
        />
        {editing === 'bays' ? (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-500 mb-1">عدد أماكن الغسيل</label>
              <input
                type="number"
                min={1}
                max={10}
                value={editBays}
                onChange={(e) => setEditBays(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                className="w-24 rounded-xl border border-gray-300 px-3 py-2 text-center text-lg font-bold focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
            {error && <p className="text-red-600 text-sm">{error}</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <InfoRow label="عدد أماكن الغسيل" value={`${shop.bayCount} مكان`} />
            <InfoRow label="وقت الغسيل المقدّر" value={`${shop.estimatedWashMinutes} دقيقة`} />
            {shop.actualAvgWashMinutes && (
              <InfoRow label="المتوسط الفعلي" value={`${shop.actualAvgWashMinutes} دقيقة`} />
            )}
          </div>
        )}
      </div>

      {/* Working Hours (editable) */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <SectionHeader
          icon={<Clock className="w-5 h-5 text-primary-600" />}
          title="ساعات العمل"
          editing={editing === 'hours'}
          onEdit={() => startEdit('hours')}
          onCancel={cancelEdit}
          onSave={saveHours}
          saving={mutation.isPending}
        />
        {editing === 'hours' ? (
          <div className="space-y-2">
            {DAY_ORDER.map((day) => {
              const val = editHours[day] || 'closed';
              const isClosed = val === 'closed';
              const [open, close] = isClosed ? ['', ''] : val.split('-');
              return (
                <div key={day} className="flex items-center gap-2 py-1.5">
                  <span className="text-sm font-medium text-gray-700 w-16">{DAY_LABELS[day]}</span>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!isClosed}
                      onChange={(e) => {
                        setEditHours((prev) => ({
                          ...prev,
                          [day]: e.target.checked ? '08:00-22:00' : 'closed',
                        }));
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-xs text-gray-400">{isClosed ? 'مغلق' : 'مفتوح'}</span>
                  </label>
                  {!isClosed && (
                    <div className="flex items-center gap-2 mr-auto">
                      <span className="text-xs text-gray-500">من</span>
                      <input
                        type="time"
                        value={open}
                        onChange={(e) => setEditHours((prev) => ({ ...prev, [day]: `${e.target.value}-${close}` }))}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                        dir="ltr"
                      />
                      <span className="text-xs text-gray-500">إلى</span>
                      <input
                        type="time"
                        value={close}
                        onChange={(e) => setEditHours((prev) => ({ ...prev, [day]: `${open}-${e.target.value}` }))}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                        dir="ltr"
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
        ) : (
          <div className="space-y-2">
            {DAY_ORDER.map((day) => {
              const val = shop.workingHours?.[day];
              const isClosed = !val || val === 'closed';
              return (
                <div key={day} className="flex items-center justify-between py-1">
                  <span className="text-sm text-gray-500">{DAY_LABELS[day]}</span>
                  {isClosed ? (
                    <span className="text-sm font-medium text-red-500">مغلق</span>
                  ) : (
                    <span className="text-sm font-medium text-gray-900">
                      {(() => {
                        const [open, close] = val.split('-');
                        return `من ${formatTime12(open)} — إلى ${formatTime12(close)}`;
                      })()}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Market Prices (read-only) */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <div className="flex items-center gap-2 mb-4">
          <Car className="w-5 h-5 text-primary-600" />
          <h2 className="text-base font-bold text-gray-900">أسعار السوق</h2>
        </div>
        <div className="space-y-4">
          <InfoRow label="سيدان" value={`${shop.sedanPrice.toLocaleString()} د.ع`} />
          <InfoRow label="SUV" value={`${shop.suvPrice.toLocaleString()} د.ع`} />
          <InfoRow label="SUV كبير" value={`${shop.largeSuvPrice.toLocaleString()} د.ع`} />
        </div>
        <p className="text-xs text-gray-400 mt-3">لتعديل الأسعار، تواصل مع إدارة المنصة</p>
      </div>
    </div>
  );
}

function SectionHeader({
  icon,
  title,
  editing,
  onEdit,
  onCancel,
  onSave,
  saving,
}: {
  icon: React.ReactNode;
  title: string;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 transition disabled:opacity-50"
          >
            {saving ? <Spinner size="sm" /> : <Check className="w-3.5 h-3.5" />}
            حفظ
          </button>
        </div>
      ) : (
        <button
          onClick={onEdit}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-primary-600 transition"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function InfoRow({ label, value, dir }: { label: string; value: string; dir?: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900" dir={dir}>{value}</span>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  dir,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  dir?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-sm text-gray-500 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir={dir}
        placeholder={placeholder}
        className="w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
      />
    </div>
  );
}
